import type { StrictRJSFSchema } from '@rjsf/utils';
import uniq from 'lodash/uniq';

import { ACCOUNT_STATUS, ACCOUNT_TYPE } from '~/consts/accountTypes';
import { AGENT_HUB_AUTH_ROLE_AFFILIATION_ADMIN, AGENT_HUB_AUTH_ROLE_AGENCY_ADMIN } from '~/consts/agentHub';
import { ROLE_CUSTOMER, ROLE_SERVICE, ROLE_VENDOR_USER } from '~/consts/roles';

import type { AccountType, UpdateAccountPayload } from '~/services/UsersService';

export type RolesAndVendors = {
  roles: Array<string>;
  vendors: Array<string>;
};

export type RolesAndVendorsErrors = {
  roles?: Array<string>;
  vendors?: Array<string>;
};

export type DisallowedRole = {
  role: string;
  message: string;
};

export function getAgentHubRolesSchema(schema: StrictRJSFSchema | undefined, savedRoles: Array<string>) {
  const items = schema?.items as (StrictRJSFSchema & { enumNames?: Array<string> }) | undefined;
  if (!schema || !items || !Array.isArray(items.enum)) {
    return schema;
  }

  const allowedRoles = [ROLE_CUSTOMER, AGENT_HUB_AUTH_ROLE_AGENCY_ADMIN, AGENT_HUB_AUTH_ROLE_AFFILIATION_ADMIN];
  const indexes = items.enum
    .map((role, index) => ({ role, index }))
    .filter(({ role }) => typeof role === 'string' && (allowedRoles.includes(role) || savedRoles.includes(role)))
    .map(({ index }) => index);

  return {
    ...schema,
    items: {
      ...items,
      enum: indexes.map((index) => items.enum[index]),
      ...(items.enumNames && { enumNames: indexes.map((index) => items.enumNames[index]) }),
    },
  };
}

export function requiresVendors(roles: Array<string>) {
  return roles.includes(ROLE_VENDOR_USER);
}

// Salesforce Account IDs are 15 or 18 characters starting with 001.
export const VENDOR_ID_PATTERN = /^001[A-Za-z0-9]{12}(?:[A-Za-z0-9]{3})?$/;

export const VENDOR_ID_MESSAGE =
  'Vendor IDs are Salesforce Account IDs and start with 001 (an ID starting with 006 is an Opportunity, not a vendor).';

export function isVendorId(value: string) {
  return VENDOR_ID_PATTERN.test(value);
}

// Dropdown rows that were added but never selected come through as empty values.
function cleanValues(values?: Array<string | null | undefined>) {
  return uniq((values ?? []).map((value) => value?.trim()).filter(Boolean));
}

export function normalizeRolesAndVendors(draft: Partial<RolesAndVendors>): RolesAndVendors {
  const roles = cleanValues(draft.roles);

  return {
    roles,
    // Vendors only apply to vendor users, so they are ignored for every other account.
    vendors: requiresVendors(roles) ? cleanValues(draft.vendors) : [],
  };
}

type ValidateRolesAndVendorsOptions = {
  accountType?: AccountType;
  disallowedRoles?: Array<DisallowedRole>;
  // The roles currently saved on the account, used to tell newly added roles apart.
  savedRoles?: Array<string>;
  // Disabled accounts have all of their roles removed, so they may be left without any.
  accountEnabled?: boolean;
};

export function validateRolesAndVendors(
  draft: Partial<RolesAndVendors>,
  { accountType, disallowedRoles = [], savedRoles = [], accountEnabled = true }: ValidateRolesAndVendorsOptions = {},
): RolesAndVendorsErrors {
  const { roles, vendors } = normalizeRolesAndVendors(draft);
  const errors: RolesAndVendorsErrors = {};

  if (!roles.length && accountEnabled) {
    errors.roles = ['At least one role is required.'];
  }

  const disallowedRole = disallowedRoles.find(({ role }) => roles.includes(role));
  if (disallowedRole) {
    errors.roles = [...(errors.roles ?? []), disallowedRole.message];
  }

  // The service role may only be added to service accounts. A service account without the role is
  // fine, and an account that already holds it is not blocked so its other roles can still be edited.
  const isAddingServiceRole = roles.includes(ROLE_SERVICE) && !savedRoles.includes(ROLE_SERVICE);
  if (isAddingServiceRole && accountType !== ACCOUNT_TYPE.SERVICE) {
    errors.roles = [
      ...(errors.roles ?? []),
      'The service role can only be added to service accounts. Change the account type first.',
    ];
  }

  if (requiresVendors(roles) && !vendors.length) {
    errors.vendors = ['Vendor users must be assigned at least one vendor.'];
  }

  const invalidVendors = requiresVendors(roles) ? vendors.filter((vendor) => !isVendorId(vendor)) : [];
  if (invalidVendors.length) {
    errors.vendors = [...(errors.vendors ?? []), `Not a vendor ID: ${invalidVendors.join(', ')}. ${VENDOR_ID_MESSAGE}`];
  }

  return errors;
}

export function hasRolesAndVendorsErrors(errors: RolesAndVendorsErrors) {
  return Boolean(errors.roles?.length || errors.vendors?.length);
}

// The account API only accepts vendors together with roles. Vendors are left out for non-vendor
// users, matching the legacy user update which never sent them in that case.
export function getRolesAndVendorsPayload(draft: Partial<RolesAndVendors>): UpdateAccountPayload {
  const { roles, vendors } = normalizeRolesAndVendors(draft);

  return requiresVendors(roles) ? { roles, vendors } : { roles };
}

/**
 * Roles and vendors are managed on the Security tab through the account API, but the legacy user
 * update still requires them. They are always taken from the latest user, never from a form draft,
 * so a save there cannot revert a role change made on the Security tab.
 */
export function getLegacyRolesAndVendors(user: Pick<App.User, 'roles' | 'vendors' | 'status'>) {
  const roles = user.roles ?? [];

  return {
    // Mirrors the legacy form, which defaulted enabled accounts without roles to customers.
    roles: !roles.length && user.status === ACCOUNT_STATUS.ENABLED ? [ROLE_CUSTOMER] : roles,
    vendors: requiresVendors(roles) ? (user.vendors ?? []) : undefined,
  };
}
